import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useParams } from 'react-router-dom';
import { useMap } from 'react-map-gl';
import { TabProps } from '@mui/material';
import { useEntityLoader } from '../../../entities/entity-form/model/useEntityLoader';
import {
    PublicTransitLayerEntityName,
    PublicTransitLineEntity,
} from '@api/entities/replancity_PublicTransitLine';
import {
    EditLineIcon,
    EditRouteIcon,
    LineRoutesIcon,
    RouteDepartureEditIcon,
    RouteDeparturesIcon,
    RouteStopsIcon,
} from '@components/icons/icons';
import EntityManagerCleaner from '../../../entities/EntityManagerCleaner';
import IconTabs, { LeftTabs } from '../../../left-panel-tabs/IconTabs';
import { isErrorResponse, isUuidString } from '@utils/utils';
import mapApi from '@api/mapApi';
import { removedFeaturesHighlighting } from '@redux/map/map-reducer';
import { useTypedSelector } from '@redux/Hooks/storeSelectors';
import { selectMapLayerIdByEntityName } from '@redux/selectors/selectors';
import { useDispatch } from 'react-redux';
import { EntityServiceName } from '@api/enums/enums';
import { useMapFiltersContext } from '@context/mapFiltersContext';
import { useNavigatePreserveSearch } from '@hooks/useNavigatePreserveSearch';

const TransitLine = memo(() => {
    const entityName = PublicTransitLayerEntityName.LINES;
    const { loadData } = useEntityLoader<PublicTransitLineEntity>({ entityName });

    const routesLayerId: string = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.ROUTES)
    );
    const stopsLayerId = useTypedSelector(state =>
        selectMapLayerIdByEntityName(state, PublicTransitLayerEntityName.STOPS)
    );

    const { lineId, routeId, departureId } = useParams();
    const existingRouteId = useMemo(() => (isUuidString(routeId) ? routeId : ''), [routeId]);

    const { highlightLayerFeatures } = useMapFiltersContext();

    const { mapboxMapId } = useMap();

    const navigate = useNavigatePreserveSearch();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const tabs: TabProps<any, LeftTabs>[] = useMemo(
        () => [
            {
                path: '',
                label: t('transit-lines.routes'),
                url: './',
                icon: <LineRoutesIcon width={16} height={16} />,
                testId: 'routesTab',
            },
            {
                path: 'edit',
                label: t('transit-lines.line-edit-form'),
                url: './edit',
                icon: <EditLineIcon width={16} height={16} />,
                testId: 'editLineTab',
            },
            ...(existingRouteId
                ? [
                      {
                          path: '/',
                          label: t('transit-lines.route-stops'),
                          url: `./${existingRouteId}/`,
                          icon: <RouteStopsIcon width={16} height={16} />,
                          testId: 'routeStopsTab',
                      },
                      {
                          path: 'edit',
                          label: t('transit-lines.route-form'),
                          url: `./${existingRouteId}/edit`,
                          icon: <EditRouteIcon width={16} height={16} />,
                          testId: 'editRouteTab',
                      },
                      {
                          path: 'departures',
                          label: t('transit-lines.departures'),
                          url: `./${existingRouteId}/departures/`,
                          icon: <RouteDeparturesIcon width={16} height={16} />,
                          testId: 'departuresTab',
                      },
                  ]
                : []),
            ...(departureId
                ? [
                      {
                          path: `${departureId}/edit`,
                          label: 'Departure',
                          url: `./${existingRouteId}/departures/${departureId}/edit`,
                          icon: <RouteDepartureEditIcon width={20} height={16} />,
                          testId: 'editDepartureTab',
                      },
                  ]
                : []),
        ],
        [existingRouteId, departureId]
    );

    useEffect(() => {
        return () => {
            highlightLayerFeatures({
                layerId: routesLayerId,
                mapboxLayerType: 'line',
            });
        };
    }, [routesLayerId, mapboxMapId, highlightLayerFeatures]);

    const highlightLine = useCallback(
        (featuresIds: number[]) => {
            highlightLayerFeatures({
                layerId: routesLayerId,
                mapboxLayerType: 'line',
                featureStateIds: featuresIds.map(id => id.toString()),
            });
        },
        [routesLayerId, mapboxMapId, highlightLayerFeatures]
    );

    useEffect(() => {
        void (async function () {
            // Loaded and highlighted only on a line page. Route should highlight itself
            if (!existingRouteId) {
                await loadData({ entityName, entityId: lineId });

                const abortController = new AbortController();
                const resp = await mapApi.getListRelatedFeatures(
                    {
                        sourceId: lineId!,
                        sourceEntityType: entityName,
                        targetEntityType: PublicTransitLayerEntityName.ROUTES,
                    },
                    EntityServiceName.PUBLIC_TRANSIT,
                    abortController.signal
                );

                if (!isErrorResponse(resp)) {
                    const { relatedFeatures } = resp;
                    highlightLine(relatedFeatures as any);
                }
            }
        })();
    }, [loadData, entityName, lineId, existingRouteId, routesLayerId, stopsLayerId, highlightLine]);

    useEffect(() => {
        return () => {
            dispatch(removedFeaturesHighlighting());
        };
    }, []);

    return (
        <>
            <IconTabs tabs={tabs} />
            <EntityManagerCleaner entityName={entityName}>
                <Outlet />
            </EntityManagerCleaner>
        </>
    );
});

export default TransitLine;
